/**
 * RDS Storage transport (Phase E, TEST_INTEGRATION_PLAN.md §29).
 *
 * The object-store half of rdsPhi.js: one function per RDS /storage route,
 * all on the same transport rule -- the signed-in user's OWN session token
 * as bearer, never a service key. RDS audits every call under the bucket
 * ('bucket:<name>') and path. Whether RDS's answer is actually SHOWN is
 * decided in rdsStorageOverlay.js (parity with the live bucket first);
 * this module is a dumb transport.
 *
 * Every function throws PhiClientError on a non-2xx and
 * PhiClientUnavailable when RDS cannot be reached, times out, or there is
 * no session -- the same two classes the PHI reads use, so the overlay's
 * fallback logic is shared. A 404 that RDS marks with `X-Rds-Tombstone: 1`
 * (the app deleted / moved that path while RDS was independent) carries
 * `tombstone: true` on the error: that object is GONE, and nothing may
 * answer for it from Supabase.
 */
import { getSessionToken, rdsBaseUrl, PhiClientError, PhiClientUnavailable } from "./rdsPhi";

/** Supabase quotes eTags ('"abc"'); RDS strips them. Compare bare. */
export function normalizeEtag(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim().replace(/^W\//, "").replace(/^"+|"+$/g, "").replace(/^'+|'+$/g, "");
  return s || null;
}

/** Each path segment percent-encoded, slashes kept, so a filename with
 * spaces, commas or apostrophes reaches RDS intact. */
export function encodeStoragePath(path) {
  return String(path || "").split("/").map(encodeURIComponent).join("/");
}

function isTombstoneHeader(headers) {
  try {
    const v = headers && typeof headers.get === "function" ? headers.get("x-rds-tombstone") : null;
    return v !== null && v !== undefined && ["1", "true", "yes"].includes(String(v).trim().toLowerCase());
  } catch (_) {
    return false;
  }
}

/** True when `err` is RDS saying the object was deleted/moved while
 * independent (a tombstoned 404). */
export function isTombstoneError(err) {
  return !!(err && err.tombstone === true && err.statusCode === 404);
}

async function readErrorDetail(resp) {
  try {
    const body = await resp.json();
    return body?.detail || "";
  } catch (_) {
    return resp.text().catch(() => "");
  }
}

/**
 * fetch with the session bearer and a hard timeout, mapped to the two
 * error classes. `parse` turns a 2xx response into the return value.
 */
async function request(path, { method = "GET", headers = {}, body, timeoutMs = 8000, parse } = {}) {
  const token = await getSessionToken();
  if (!token) throw new PhiClientUnavailable("no active session to forward to RDS");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let resp;
    try {
      resp = await fetch(`${rdsBaseUrl()}${path}`, {
        method,
        headers: { Authorization: `Bearer ${token}`, ...headers },
        body,
        signal: controller.signal,
      });
    } catch (err) {
      if (err?.name === "AbortError") throw new PhiClientUnavailable("RDS timed out");
      throw new PhiClientUnavailable(`RDS unreachable: ${err.message}`);
    }
    if (resp.ok) {
      try {
        return await parse(resp);
      } catch (err) {
        if (err?.name === "AbortError") throw new PhiClientUnavailable("RDS timed out");
        throw new PhiClientError(resp.status, "RDS returned an unreadable 200 response");
      }
    }
    // Read the header before the body: a tombstoned 404 is "deleted", not
    // merely "never held" (older RDS never sends it).
    const tombstone = resp.status === 404 && isTombstoneHeader(resp.headers);
    const err = new PhiClientError(resp.status, await readErrorDetail(resp));
    if (tombstone) err.tombstone = true;
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

function bucketPath(bucket) {
  return `/storage/${encodeURIComponent(bucket)}`;
}

/**
 * GET one object. Resolves to {blob, etag, sourceEtag, md5, size,
 * contentType, schema}; `etag` is RDS's parity value (the Supabase eTag
 * recorded at sync, else the md5 of the bytes), already bare.
 * @throws {PhiClientError} 404 when RDS does not hold the object.
 */
export async function getObject(bucket, path, { timeoutMs = 8000 } = {}) {
  if (!bucket || !path) throw new Error("bucket and path are required");
  return request(`${bucketPath(bucket)}/${encodeStoragePath(path)}`, {
    timeoutMs,
    parse: async (resp) => ({
      blob: await resp.blob(),
      etag: normalizeEtag(resp.headers.get("etag")),
      sourceEtag: normalizeEtag(resp.headers.get("x-source-etag")),
      md5: resp.headers.get("x-content-md5") || null,
      size: Number(resp.headers.get("x-byte-size") || 0),
      contentType: resp.headers.get("content-type") || "application/octet-stream",
      schema: resp.headers.get("x-schema") || null,
    }),
  });
}

/**
 * PUT one object's bytes (a mirrored upload, or the overlay's read-repair).
 * `sourceEtag`, when the live object's eTag is known, is recorded so the
 * next parity check needs no repair. Resolves to RDS's {ok, byte_size,
 * content_md5, ...} payload.
 */
export async function putObject(bucket, path, body, { contentType, sourceEtag, timeoutMs = 30000 } = {}) {
  if (!bucket || !path) throw new Error("bucket and path are required");
  if (body === undefined || body === null) throw new Error("body is required");
  const headers = { "Content-Type": contentType || (body && body.type) || "application/octet-stream" };
  const etag = normalizeEtag(sourceEtag);
  if (etag) headers["X-Source-Etag"] = etag;
  return request(`${bucketPath(bucket)}/${encodeStoragePath(path)}`, {
    method: "PUT", headers, body, timeoutMs, parse: (resp) => resp.json(),
  });
}

/**
 * The immediate children of `prefix`, shaped like Supabase's list() rows.
 * `opts` takes Supabase's {limit, offset, search, sortBy: {column, order}}.
 * Resolves to {rows, count, tombstones}: `tombstones` are the names under
 * `prefix` the app deleted / moved while RDS was independent (empty from
 * an RDS that predates tombstones).
 */
export async function listObjects(bucket, prefix = "", opts = {}, { timeoutMs = 8000 } = {}) {
  if (!bucket) throw new Error("bucket is required");
  const q = new URLSearchParams();
  if (prefix) q.set("prefix", String(prefix).replace(/^\/+|\/+$/g, ""));
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  if (opts.search) q.set("search", String(opts.search));
  if (opts.sortBy?.column) q.set("sort_column", String(opts.sortBy.column));
  if (opts.sortBy?.order) q.set("sort_order", String(opts.sortBy.order).toLowerCase());
  q.set("include_tombstones", "1");
  const qs = q.toString();
  return request(`${bucketPath(bucket)}${qs ? `?${qs}` : ""}`, {
    timeoutMs,
    parse: async (resp) => {
      const body = await resp.json();
      return {
        rows: Array.isArray(body?.rows) ? body.rows : [],
        count: Number(body?.count || 0),
        tombstones: Array.isArray(body?.tombstones) ? body.tombstones.filter(Boolean).map(String) : [],
      };
    },
  });
}

/**
 * The tombstoned names of a listing as a Set of names RELATIVE to `prefix`
 * (RDS may answer bare names or full paths; both are accepted).
 */
export function tombstoneNames(tombstones, prefix = "") {
  const dir = String(prefix || "").replace(/^\/+|\/+$/g, "");
  const out = new Set();
  (tombstones || []).forEach((t) => {
    const s = String(t || "").replace(/^\/+/, "");
    if (!s) return;
    out.add(s);
    if (dir && s.startsWith(`${dir}/`)) out.add(s.slice(dir.length + 1));
  });
  return out;
}

/**
 * Does RDS hold `path`, and is it tombstoned? One list() of its folder, no
 * body download. Resolves to {exists, tombstoned, row}.
 */
export async function objectExists(bucket, path, { timeoutMs = 8000 } = {}) {
  if (!bucket || !path) throw new Error("bucket and path are required");
  const clean = String(path).replace(/^\/+/, "");
  const i = clean.lastIndexOf("/");
  const dir = i < 0 ? "" : clean.slice(0, i);
  const base = i < 0 ? clean : clean.slice(i + 1);
  const { rows, tombstones } = await listObjects(bucket, dir, { search: base, limit: 1000 }, { timeoutMs });
  // Folders come back with id null; only a file of that name counts.
  const row = (rows || []).find((r) => r && r.name === base && r.id) || null;
  return { exists: !!row, tombstoned: !row && tombstoneNames(tombstones, dir).has(base), row };
}

function jsonRequest(path, payload, timeoutMs) {
  return request(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    timeoutMs,
    parse: (resp) => resp.json(),
  });
}

/** Drop objects RDS holds (a mirrored remove). Resolves to {removed}. */
export async function removeObjects(bucket, paths, { timeoutMs = 8000 } = {}) {
  if (!bucket) throw new Error("bucket is required");
  const clean = (paths || []).filter(Boolean).map(String);
  if (!clean.length) return { removed: [] };
  return jsonRequest(`${bucketPath(bucket)}/remove`, { paths: clean }, timeoutMs);
}

/** Rename an object RDS holds (a mirrored move). */
export async function moveObject(bucket, fromPath, toPath, { timeoutMs = 8000 } = {}) {
  if (!bucket || !fromPath || !toPath) throw new Error("bucket, fromPath and toPath are required");
  return jsonRequest(`${bucketPath(bucket)}/move`, { from_path: fromPath, to_path: toPath }, timeoutMs);
}

/** Duplicate an object RDS holds (a mirrored copy). */
export async function copyObject(bucket, fromPath, toPath, { timeoutMs = 8000 } = {}) {
  if (!bucket || !fromPath || !toPath) throw new Error("bucket, fromPath and toPath are required");
  return jsonRequest(`${bucketPath(bucket)}/copy`, { from_path: fromPath, to_path: toPath }, timeoutMs);
}

/**
 * Operator: mirror every bucket (or `buckets`) into RDS now
 * (POST /admin/sync-storage). Incremental -- unchanged objects are not
 * re-downloaded -- but a first run of the large buckets takes long, hence
 * the generous timeout. 409 = a run is already going.
 */
export async function syncStorage({ buckets, prune = true, timeoutMs = 3600000 } = {}) {
  const payload = { prune: !!prune };
  if (Array.isArray(buckets) && buckets.length) payload.buckets = buckets.map(String);
  return jsonRequest("/admin/sync-storage", payload, timeoutMs);
}
